/**
 * Capability preview API client (CR5b)
 *
 * POST /api/capability/resolve
 * POST /api/capability/batch-resolve
 */

import { API_REQUEST_TIMEOUT_MS } from '../config/api';
import { fetchWithAuth } from './authFetch';
import type { CapabilityDecision, PartialCapabilityDecision } from '@shared/capability';
import { normalizeCapabilityDecision } from '@shared/capability';
import { resolveApiOrigin } from '@shared/api/resolve-api-origin';

export const CAPABILITY_BATCH_RESOLVE_MAX = 50;

/** Request body context — tenant/entity come from auth headers */
export interface CapabilityResolveContext {
  action: string;
  domain: string;
  plane?: string;
  objectId?: string;
  fieldKey?: string;
  taskExecutionId?: string;
  flowExecutionId?: string;
  operationExecutionId?: string;
  virtualSpaceId?: string;
}

const baseUrl = (): string => resolveApiOrigin();

async function parseJson<T>(res: Response): Promise<T> {
  const body = (await res.json()) as {
    success?: boolean;
    data?: T;
    message?: string;
    code?: string;
    error?: string;
  };
  if (!res.ok || body.success === false) {
    throw new Error(body.message ?? body.code ?? body.error ?? `HTTP ${res.status}`);
  }
  if (body.data === undefined) {
    throw new Error('응답 data가 없습니다');
  }
  return body.data;
}

export async function fetchCapabilityResolve(
  tenantId: string,
  context: CapabilityResolveContext
): Promise<CapabilityDecision> {
  const res = await fetchWithAuth(`${baseUrl()}/api/capability/resolve`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-tenant-id': tenantId,
    },
    body: JSON.stringify(context),
    signal: AbortSignal.timeout(API_REQUEST_TIMEOUT_MS),
  });
  const data = await parseJson<{ decision: PartialCapabilityDecision }>(res);
  return normalizeCapabilityDecision(data.decision);
}

export async function fetchCapabilityBatchResolve(
  tenantId: string,
  contexts: CapabilityResolveContext[]
): Promise<CapabilityDecision[]> {
  if (contexts.length === 0) {
    throw new Error('contexts must not be empty');
  }
  if (contexts.length > CAPABILITY_BATCH_RESOLVE_MAX) {
    throw new Error(`contexts must not exceed ${CAPABILITY_BATCH_RESOLVE_MAX}`);
  }

  const res = await fetchWithAuth(`${baseUrl()}/api/capability/batch-resolve`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-tenant-id': tenantId,
    },
    body: JSON.stringify({ contexts }),
    signal: AbortSignal.timeout(API_REQUEST_TIMEOUT_MS),
  });
  const data = await parseJson<{ decisions: PartialCapabilityDecision[] }>(res);
  return data.decisions.map((decision) => normalizeCapabilityDecision(decision));
}

type DebouncerPending = {
  tenantId: string;
  context: CapabilityResolveContext;
  resolve: (decision: CapabilityDecision) => void;
  reject: (error: Error) => void;
};

/** Debounced batch preview — coalesces resolve calls within `ms` window */
export function createCapabilityPreviewDebouncer(ms: number) {
  let timer: ReturnType<typeof setTimeout> | null = null;
  const pending: DebouncerPending[] = [];

  const flush = async (): Promise<void> => {
    const batch = pending.splice(0);
    timer = null;
    if (batch.length === 0) {
      return;
    }

    const byTenant = new Map<string, DebouncerPending[]>();
    for (const item of batch) {
      const list = byTenant.get(item.tenantId) ?? [];
      list.push(item);
      byTenant.set(item.tenantId, list);
    }

    for (const [tenantId, items] of byTenant) {
      try {
        for (let offset = 0; offset < items.length; offset += CAPABILITY_BATCH_RESOLVE_MAX) {
          const chunkItems = items.slice(offset, offset + CAPABILITY_BATCH_RESOLVE_MAX);
          const contexts = chunkItems.map((item) => item.context);
          const decisions = await fetchCapabilityBatchResolve(tenantId, contexts);
          chunkItems.forEach((item, index) => {
            const decision = decisions[index];
            if (decision) {
              item.resolve(decision);
            } else {
              item.reject(new Error('capability preview decision missing'));
            }
          });
        }
      } catch (error: unknown) {
        const err = error instanceof Error ? error : new Error(String(error));
        items.forEach((item) => {
          item.reject(err);
        });
      }
    }
  };

  return (tenantId: string, context: CapabilityResolveContext): Promise<CapabilityDecision> =>
    new Promise((resolve, reject) => {
      pending.push({ tenantId, context, resolve, reject });
      if (timer !== null) {
        clearTimeout(timer);
      }
      timer = setTimeout(() => {
        void flush();
      }, ms);
    });
}
